import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const locStorageKey = user?.id ? `ld_locations_${user.id}` : "ld_locations";
  const activeLocStorageKey = user?.id ? `ld_active_location_id_${user.id}` : "ld_active_location_id";

  const buildUserFallbackLocation = (u) => {
    if (!u) return null;
    return {
      id: u.active_location_id || "loc_primary",
      name: u.location_name || `${u.name}'s Studio`,
      city: u.location_city?.split(",")[0]?.trim() || "",
      state: u.location_city?.split(",")[1]?.trim() || "",
      address: "",
      phone: "",
      category: "Digital Marketing & Commercial Studio",
      is_primary: true,
      contacts_count: 0,
      revenue: 0,
    };
  };

  const [locations, setLocations] = useState(() => {
    try {
      const saved = localStorage.getItem(locStorageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  const [activeLocationId, setActiveLocationId] = useState(() => {
    return localStorage.getItem(activeLocStorageKey) || user?.active_location_id || "";
  });

  // Dynamically resolve active location: matched from list, or user fallback, or clean empty placeholder
  const activeLocation =
    locations.find((l) => l.id === activeLocationId) ||
    locations[0] ||
    buildUserFallbackLocation(user) || {
      id: "loc_new",
      name: user?.name ? `${user.name}'s Studio` : "My Studio",
      city: "",
      state: "",
      address: "",
      phone: "",
      category: "Marketing Studio",
      is_primary: true,
      contacts_count: 0,
      revenue: 0,
    };

  const loadLocations = useCallback(async () => {
    if (!user) {
      setLocations([]);
      return;
    }
    try {
      const res = await api.get("/locations");
      if (res.data && res.data.length > 0) {
        setLocations(res.data);
        localStorage.setItem(locStorageKey, JSON.stringify(res.data));

        const userLocId = user.active_location_id || localStorage.getItem(activeLocStorageKey);
        if (userLocId && res.data.some(l => l.id === userLocId)) {
          setActiveLocationId(userLocId);
        } else {
          setActiveLocationId(res.data[0].id);
        }
      } else {
        const fallback = buildUserFallbackLocation(user);
        if (fallback) setLocations([fallback]);
      }
    } catch {
      const fallback = buildUserFallbackLocation(user);
      if (fallback) setLocations([fallback]);
    }
  }, [user, locStorageKey, activeLocStorageKey]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const switchLocation = async (locId) => {
    const target = locations.find((l) => l.id === locId);
    if (!target) return;

    setActiveLocationId(locId);
    localStorage.setItem("ld_active_location_id", locId);

    try {
      await api.post(`/locations/${locId}/switch`);
    } catch {
      // local state still updated
    }

    toast.success(`Switched account to ${target.name} (${target.city}, ${target.state})`);
  };

  const createLocation = async (data) => {
    try {
      const payload = {
        name: data.name.trim(),
        city: data.city.trim(),
        state: data.state.trim(),
        address: data.address?.trim() || "",
        phone: data.phone?.trim() || "",
        category: data.category || "Commercial Studio",
        timezone: data.timezone || "America/New_York",
      };

      let newLoc;
      try {
        const res = await api.post("/locations", payload);
        newLoc = res.data;
      } catch (err) {
        if (err?.response?.status === 402) {
          throw err;
        }
        newLoc = {
          id: `loc_${Date.now().toString(36)}`,
          ...payload,
          is_primary: false,
          created_at: new Date().toISOString(),
          contacts_count: 0,
          revenue: 0,
        };
      }

      const updated = [newLoc, ...locations];
      setLocations(updated);
      localStorage.setItem(locStorageKey, JSON.stringify(updated));
      setActiveLocationId(newLoc.id);
      localStorage.setItem(activeLocStorageKey, newLoc.id);

      toast.success(`Location "${newLoc.name}" created and set as active workspace!`);
      return newLoc;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create location");
      throw err;
    }
  };

  const updateLocation = async (locId, data) => {
    try {
      const payload = {
        name: data.name?.trim(),
        city: data.city?.trim(),
        state: data.state?.trim(),
        address: data.address?.trim() || "",
        phone: data.phone?.trim() || "",
        category: data.category || "Commercial Studio",
        timezone: data.timezone || "America/New_York",
      };

      let updatedLoc;
      try {
        const res = await api.patch(`/locations/${locId}`, payload);
        updatedLoc = res.data;
      } catch (err) {
        const existing = locations.find((l) => l.id === locId) || {};
        updatedLoc = { ...existing, ...payload };
      }

      const updatedList = locations.map((l) => (l.id === locId ? { ...l, ...updatedLoc } : l));
      setLocations(updatedList);
      localStorage.setItem(locStorageKey, JSON.stringify(updatedList));

      toast.success(`Location "${updatedLoc.name}" updated successfully!`);
      return updatedLoc;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update location");
      throw err;
    }
  };

  return (
    <LocationContext.Provider
      value={{
        locations,
        activeLocation,
        switchLocation,
        createLocation,
        updateLocation,
        loadLocations,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export const useLocationContext = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error("useLocationContext must be used within a LocationProvider");
  }
  return ctx;
};

